import { useCallback } from 'react';

import { useConfiguration } from '@ocx/data-configuration';
import { PointsStrategy } from '@ocx/graphql';
import {
  ActionType,
  CustomDataWithAction,
  hasOfferDetailedMarketingContent,
  isCustomDataWithAction,
  isOfferSweepstakesPoints,
  isOfferSweepstakesProduct,
  isOfferWithExternalUrl,
  isPromocodesOffer,
  isSpinWheelGameOffer,
  Offer,
} from '@ocx/data-offers';
import { useEditProfilePopup } from '@ocx/feat-edit-profile';
import { useRudderStack } from '@ocx/data-analytics';
// eslint-disable-next-line @nx/enforce-module-boundaries -- app navigation routes
import { useGetNavigationActions } from '@ocx-app/hooks/useGetNavigationActions';

import { useOfferNavigateTo } from './use-offer-navigate-to.hook';

const useHandleCustomDataAction = () => {
  const navigateTo = useOfferNavigateTo();
  const { open: openEditProfilePopup } = useEditProfilePopup();

  return (customData: CustomDataWithAction) => {
    const { action } = customData;
    if (action.type === ActionType.NavigateTo) {
      navigateTo({ url: action.payload.url, isInAppLink: !action.payload.openURLInWebView });
    }

    if (action.type === ActionType.RequestCompleteProfile) {
      openEditProfilePopup({ returnUrl: action?.payload?.return_url });
    }
  };
};

export type OfferOnClick = (() => unknown) | undefined;
export type GetOfferOnClick = (offer: Offer) => OfferOnClick;

export type UseGetOfferOnClick = (params: { pointsStrategy: PointsStrategy }) => GetOfferOnClick;
export const useGetOfferOnClick: UseGetOfferOnClick = ({ pointsStrategy }) => {
  /**
   * TODO: Initially designed as a pure function, but it uses RudderStack and navigation actions.
   * Consider revisiting this design.
   */
  const isPromocodesEnabled = useConfiguration('loyaltyProgram.isPromocodesEnabled');
  const handleOfferCustomDataAction = useHandleCustomDataAction();
  const navigateTo = useOfferNavigateTo();
  const { pushToOfferDetails, pushToRedeemPromocode } = useGetNavigationActions();

  const { triggerEvent } = useRudderStack();

  const trackOfferClickedEvent = useCallback(
    (offer: Offer) => {
      triggerEvent('offer_clicked', { offerId: offer.id });
    },
    [triggerEvent],
  );

  return useCallback(
    (offer: Offer) => {
      const hasDetails = hasOfferDetailedMarketingContent(offer.marketingContent);

      if (isPromocodesOffer(offer) && isPromocodesEnabled) {
        return () => {
          pushToRedeemPromocode();
          trackOfferClickedEvent(offer);
        };
      }

      if (isOfferWithExternalUrl(offer)) {
        return () => {
          navigateTo({ url: offer.marketingContent.externalUrl, isInAppLink: false });
          trackOfferClickedEvent(offer);
        };
      }

      const { customData } = offer;
      if (isCustomDataWithAction(customData)) {
        return () => {
          handleOfferCustomDataAction(customData);
          trackOfferClickedEvent(offer);
        };
      }

      if (isOfferSweepstakesPoints(offer) && pointsStrategy !== PointsStrategy.NotUsed) {
        return () => {
          pushToOfferDetails(offer.id, { shouldHistoryPopOnClose: true });
          trackOfferClickedEvent(offer);
        };
      }

      if (isOfferSweepstakesProduct(offer) && hasDetails) {
        return () => {
          pushToOfferDetails(offer.id, { shouldHistoryPopOnClose: true });
          trackOfferClickedEvent(offer);
        };
      }

      if (hasDetails || isSpinWheelGameOffer(offer)) {
        return () => {
          pushToOfferDetails(offer.id, { shouldHistoryPopOnClose: true });
          trackOfferClickedEvent(offer);
        };
      }

      return undefined;
    },
    [
      isPromocodesEnabled,
      pointsStrategy,
      pushToRedeemPromocode,
      trackOfferClickedEvent,
      navigateTo,
      handleOfferCustomDataAction,
      pushToOfferDetails,
    ],
  );
};
